import { useEffect } from "react";
import {
  type EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import { getMe } from "@/api/userService";
import { queryKeys } from "./lib/queryKeys";
import { useLogin } from "@/hooks/logActivityHooks";

export default function Auth() {
  const { instance, inProgress, accounts } = useMsal();
  const qc = useQueryClient();
  const { mutate: loginMutate } = useLogin();

  useEffect(() => {
    if (accounts?.length && instance.getActiveAccount() == null) {
      instance.setActiveAccount(accounts[0]);
    }
    if (accounts?.length && inProgress === InteractionStatus.None) {
      qc.prefetchQuery({ queryKey: queryKeys.user.me(), queryFn: getMe });
    }
  }, [accounts, inProgress, instance, qc]);

  useEffect(() => {
    const cbId = instance.addEventCallback(async (e: EventMessage) => {
      if (
        e.eventType === EventType.LOGIN_SUCCESS ||
        e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const account =
          e.payload && "account" in e.payload ? e.payload.account : null;
        if (account) instance.setActiveAccount(account);
        
        if (e.eventType === EventType.LOGIN_SUCCESS) {
          try {
            await instance.acquireTokenSilent({
              scopes: ["User.Read"],
              account: account!,
            });
            
            await qc.prefetchQuery({ queryKey: queryKeys.user.me(), queryFn: getMe });
            loginMutate();
            console.log("Login successful, user info prefetched, and login activity logged.");
          } catch (error) {
            console.error("Failed to acquire token or prefetch user data:", error);
          }
        } else {
          qc.prefetchQuery({ queryKey: queryKeys.user.me(), queryFn: getMe });
        }
      }
    });
    return () => {
      if (cbId) instance.removeEventCallback(cbId);
    };
  }, [instance, qc, loginMutate]);

  return null;
}
